import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import { UseProspectReminders } from "../../../../hooks/useProspectReminders";
import { OnboardingReminders, ReminderRung, ReminderTrigger } from "../../../../types/onboarding";
import { NextStepAction } from "../../../../utils/prospectNextStep";
import { formatInstant } from "../../../../utils/instantFormat";
import {
  formatIsoDate,
  nextRung,
  previewTypeFor,
  rungStatusLabel,
  rungStatusSeverity,
  waitingOn,
} from "../../../../utils/reminders";
import EmailPreviewDialog from "./EmailPreviewDialog";
import LogCallDialog from "./LogCallDialog";
import SendNextReminderDialog from "./SendNextReminderDialog";
import SnoozeRemindersDialog from "./SnoozeRemindersDialog";
import { reminderErrorMessage } from "./reminderErrors";

interface RemindersCardProps {
  prospectId: string;
  // Undefined from a backend that predates CWA-189; null when it could not read the ladder.
  reminders: OnboardingReminders | null | undefined;
  moreInfoNeeded: boolean;
  actions: UseProspectReminders;
  requestedAction?: NextStepAction | null;
  onRequestHandled?: () => void;
}

type Open = "send" | "preview" | "snooze" | "call" | null;

const triggerLabel = (trigger: ReminderTrigger | null): string | null =>
  trigger === "MANUAL" ? "Manual" : trigger === "SCHEDULE" ? "Automatic" : null;

const days = (n: number) => `${n} ${n === 1 ? "day" : "days"}`;

const RungRow: React.FC<{ rung: ReminderRung }> = ({ rung }) => {
  const trigger = rung.sent_at ? triggerLabel(rung.trigger) : null;
  return (
    <li className="flex flex-wrap align-items-center gap-2 py-2 border-bottom-1 surface-border" data-testid="reminder-rung">
      <span className="font-medium w-12rem">{rung.label}</span>
      <Tag value={rungStatusLabel(rung.status)} severity={rungStatusSeverity(rung.status)} />
      {rung.sent_at ? (
        <span className="text-sm">Sent {formatInstant(rung.sent_at)}</span>
      ) : (
        rung.due_at && <span className="text-sm text-500">Due {formatInstant(rung.due_at)}</span>
      )}
      {trigger && <Tag value={trigger} severity={trigger === "Manual" ? "warning" : "info"} rounded />}
      {rung.status === "FAILED" && (
        <span className="text-sm p-error">
          {rung.failure ?? "Failed"}
          {rung.attempts > 0 && ` (${rung.attempts} ${rung.attempts === 1 ? "attempt" : "attempts"})`}
        </span>
      )}
    </li>
  );
};

/**
 * CWA-189: what the onboarding reminders have done for this prospect's current
 * step, and the ops controls over them — send the next rung now, snooze, log a
 * follow-up call, and mark requested information as received.
 *
 * Separate from the contract tab's manual chase ("Log reminder"): that records
 * a contact ops made themselves; this is the automatic ladder.
 */
const RemindersCard: React.FC<RemindersCardProps> = ({
  prospectId,
  reminders,
  moreInfoNeeded,
  actions,
  requestedAction = null,
  onRequestHandled,
}) => {
  const [open, setOpen] = useState<Open>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (requestedAction !== "follow-up-call") return;
    setOpen("call");
    onRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedAction]);

  if (reminders === undefined) return null;
  if (reminders === null) {
    return (
      <Message
        severity="warn"
        className="w-full mb-4"
        text="The reminders could not be loaded. Reload the page to try again."
      />
    );
  }

  const { step } = reminders;
  const next = nextRung(reminders);
  const previewType = previewTypeFor(next);
  const close = () => setOpen(null);

  const resolve = async () => {
    setResolving(true);
    setError(null);
    const result = await actions.resolveMoreInfo();
    setResolving(false);
    if (!result.ok) setError(result.message ?? null);
  };

  return (
    <div className="mb-4 onboarding-section" data-testid="reminders-card">
      <div className="flex flex-wrap align-items-center gap-2 mb-2">
        <h3 className="m-0">Reminders</h3>
        {reminders.needs_follow_up && <Tag severity="danger" value="Needs follow-up" />}
        {reminders.snoozed_until && (
          <Tag severity="info" icon="pi pi-clock" value={`Snoozed until ${formatIsoDate(reminders.snoozed_until)}`} />
        )}
      </div>

      {!reminders.enabled && (
        <Message severity="info" className="w-full mb-3" text="Automatic reminders are off on this environment." />
      )}
      {error && <Message severity="error" className="w-full mb-3" text={error} />}

      {step ? (
        <p className="mt-0">
          <strong>{waitingOn(step.owner)}</strong>: {step.label} · waiting {days(step.days_waiting)}
          <span className="text-500"> (since {formatInstant(step.clock_started_at)})</span>
        </p>
      ) : (
        <p className="mt-0">Nothing is waiting, so no reminders are due.</p>
      )}

      {step && reminders.rungs.length > 0 && (
        <ul className="list-none p-0 m-0 mb-3" aria-label="Reminder steps">
          {reminders.rungs.map((rung) => (
            <RungRow key={rung.rung} rung={rung} />
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        {step && next && reminders.enabled && (
          <Button label="Send next reminder now" icon="pi pi-send" outlined onClick={() => setOpen("send")} />
        )}
        {step && <Button label="Snooze" icon="pi pi-clock" outlined onClick={() => setOpen("snooze")} />}
        {step && <Button label="Log call" icon="pi pi-phone" outlined onClick={() => setOpen("call")} />}
        {moreInfoNeeded && (
          <Button
            label="Information received"
            icon="pi pi-check"
            outlined
            loading={resolving}
            disabled={resolving}
            onClick={resolve}
          />
        )}
      </div>

      {reminders.history.length > 0 && (
        <div className="mt-3">
          <Button
            label={`${showHistory ? "Hide" : "Show"} history (${reminders.history.length})`}
            icon={showHistory ? "pi pi-chevron-up" : "pi pi-chevron-down"}
            text
            size="small"
            onClick={() => setShowHistory((v) => !v)}
          />
          {showHistory && (
            <ul className="list-none p-0 m-0" aria-label="Reminder history">
              {reminders.history.map((entry, i) => (
                <li key={`${entry.step_key}-${entry.rung}-${i}`} className="flex flex-wrap gap-2 py-1 text-sm">
                  <span className="text-500">{formatInstant(entry.at)}</span>
                  <span>{entry.step_key}</span>
                  <span>{entry.rung}</span>
                  <Tag value={rungStatusLabel(entry.status)} severity={rungStatusSeverity(entry.status)} />
                  {triggerLabel(entry.trigger) && <span className="text-500">{triggerLabel(entry.trigger)}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {step && next && (
        <SendNextReminderDialog
          visible={open === "send"}
          rung={next}
          owner={step.owner}
          onHide={close}
          onSend={actions.sendNext}
          onPreview={() => setOpen("preview")}
        />
      )}
      {previewType && next && open === "preview" && (
        <EmailPreviewDialog
          visible
          prospectId={prospectId}
          type={previewType}
          header={`Preview: ${next.label}`}
          sendLabel="Send now"
          onSend={async () => {
            const result = await actions.sendNext();
            if (!result.ok) setError(result.message ?? null);
          }}
          onHide={close}
          errorMessage={reminderErrorMessage}
        />
      )}
      <SnoozeRemindersDialog
        visible={open === "snooze"}
        snoozedUntil={reminders.snoozed_until}
        onHide={close}
        onSnooze={actions.snooze}
      />
      <LogCallDialog visible={open === "call"} onHide={close} onSubmit={actions.logCall} />
    </div>
  );
};

export default RemindersCard;
